<template>

    <div class="route-user-form">
        <div class="padding-box">

            <cms-heading class="page-heading" level="1" :title="pageTitle" icon="magicon-user">
                <cms-button color="positive" @click="submit" :loading="submitting">Opslaan</cms-button>
                <cms-button color="medium-dark" type="bordered" :to="{ name: 'users' }">Annuleren</cms-button>
                <cms-button color="negative" type="bordered" v-if="itemId && isAdmin" @click="deleteUser" style="margin-right: 10px;">Gebruiker verwijderen</cms-button>
                <cms-button color="negative" type="bordered" v-if="itemId" @click="sendNewPassword">Wachtwoord reset mail sturen</cms-button>
            </cms-heading>

            <div class="wm-block" v-spinner="loading">
                <cms-form @submit="submit" v-if="!loading">
                    <cms-form-input title="Voornaam" name="firstName" v-model="editUser.firstName"></cms-form-input>

                    <cms-form-input title="Achternaam" name="lastName" v-model="editUser.lastName"></cms-form-input>

                    <cms-form-input title="Initialen" name="initials" v-model="editUser.initials"></cms-form-input>

                    <cms-form-input title="E-mail adres" name="email" v-model="editUser.email"
                                    v-validate="'required|email'" data-vv-as="E-mail adres"
                                    :error="error('email')"></cms-form-input>

                    <template v-if="itemId">

                        <cms-form-input title="Nieuw wachtwoord" type="password" name="password" placeholder="Optioneel" v-model="editUser.password"></cms-form-input>

                        <cms-form-input v-if="(editUser.password && editUser.password.length) || !itemId" title="Wachtwoord herhalen" type="password" name="passwordRepeat" v-model.trim="passwordRepeat"
                                        data-vv-as="Herhaalde wachtwoord"
                                        :error="error('passwordRepeat')"></cms-form-input>
                    </template>

                    <cms-form-dropdown v-if="isAdmin" :value="getApplicationRoleValue(application)" @input="onApplicationRoleUpdate(application, $event)" v-for="application in applications" :key="application.value"
                                       :title="application | application" :options="getApplicationRoles(application)"
                                       :empty="application != 'MIJN_AVECO' ? `Geen toegang` : null"></cms-form-dropdown>

                </cms-form>
            </div>

        </div>
    </div>

</template>

<script>

    import Component from 'vue-class-component'
    import Vue from 'global-vue'

    import find from 'lodash/find'
    import pick from 'lodash/pick'
    import clone from 'lodash/clone'

    import createQuery from '@wittignl/js-core/apollo/queries/createQuery'
    import FindUserQuery from 'gql/queries/UserForm/FindUser.gql'

    import UserMutations from 'app/mutations/UserMutations'

    import UserRole from 'app/enums/UserRole'
    import Application from 'app/enums/Application'

    import userRoleFilter from 'app/filters/userRole'

    import validationMixin from '@wittignl/js-core/mixins/validation'
    import getGraphQLError from 'app/utils/getGraphQLError';
    import ApiError from 'app/enums/ApiError';
    import UserPasswordRequestMutations from 'app/mutations/UserPasswordRequestMutations';

    @Component({

        name: 'user-form',
        mixins: [validationMixin],
        apollo: {

            user: createQuery(FindUserQuery, {

                fetchPolicy: 'network-only',

                skip(){
                    return !this.itemId;
                },

                response(response){

                    this.user = clone(response);

                    this.editUser = {
                        ...pick(response, Object.keys(this.editUser)),
                        password: null
                    }

                    this.applicationRoles = response.userRoles.items.map(role => {

                        return {
                            application: role.application,
                            role: role.role
                        }
                    })
                },

                variables(){

                    return {
                        userId: this.itemId
                    }
                }
            })
        }
    })
    export default class extends Vue
    {
        data(){

            return {

                user: null,
                editUser: {
                    email: null,
                    password: null,
                    firstName: null,
                    lastName: null,
                    initials: null
                },

                applicationRoles: [],

                passwordRepeat: null,

                submitting: false,
                submitted: false,

                loading: 0
            }
        }

        breadcrumb(){

            return [{
                title: 'Gebruikers',
                route: {
                    name: 'users'
                }
            }, this.pageTitle];
        }

        created(){

            this.logger = this.$di.logger.child('UserForm');
        }

        mounted() {

            if(!this.isAdmin && this.itemId !== this.$user.id) {

                this.$router.replace({
                    name: 'account'
                });
            }
        }

        get pageTitle(){

            var pageTitle = null;

            if(this.itemId){
                pageTitle = this.user ? this.user.firstName + ' ' + this.user.lastName : 'Gebruiker wijzigen';
            }
            else {
                pageTitle = 'Nieuwe gebruiker toevoegen';
            }

            return pageTitle;
        }

        get itemId(){

            if(this.$route.name === 'account') {
                return this.$user.id;
            }

            return this.$route.params.id;
        }

        get roleOptions(){

            return UserRole.options();
        }

        onQueryError(){

            this.$toast.error('De gebruiker kon niet worden opgehaald');
        }

        getApplicationRoleValue(application) {

            let applicationRole = find(this.applicationRoles, { application });

            return applicationRole ? applicationRole.role : (application === Application.MIJN_AVECO ? UserRole.USER : null);
        }

        onApplicationRoleUpdate(application, role) {

            let applicationRole = find(this.applicationRoles, { application });
            if(!applicationRole) {

                this.applicationRoles.push({
                    application,
                    role
                });
            }
            else {

                applicationRole.role = role;
            }
        }

        async submit(){

            this.submitted = true;

            const success = await this.$validator.validateAll();

            if (!success) {

                this.$toast.warning('Niet alle velden zijn correct ingevuld');
                return;
            }

            if (this.isSettingNewPassword && this.editUser.password !== this.passwordRepeat) {

                this.$toast.warning('Het wachtwoord en het herhaalde wachtwoord komen niet overeen');
                return;
            }

            this.submitting = true;
            const input = clone(this.editUser);
            const roles = clone(this.applicationRoles).filter(applicationRole => applicationRole.role);

            try {

                if (this.user) {

                    input.id = this.user.id;

                    if (!input.password) {
                        delete input.password;
                    }

                    if(this.isAdmin) {

                        await UserMutations.updateUserRoles(this.user.id, roles);
                    }
                    await UserMutations.update(input);

                    this.logger.info('User updated');
                    this.$toast.success('De gebruiker is opgeslagen');
                }
                else {

                    delete input.password;

                    var createdUser = await UserMutations.create(input);
                    if(this.isAdmin) {
                        await UserMutations.updateUserRoles(createdUser.data.createUser.id, roles);
                    }
                    this.logger.info('User created');
                    this.$toast.success('De gebruiker is aangemaakt');
                }

                const route = this.$route.name !== 'account' ? { name: 'users' } : null;

                if(route) {
                    this.$router.replace(route);
                }
                else {

                    this.$refetchQuery("user");
                }
            }
            catch(e){

                const errorCode = getGraphQLError(e);
                let graphQLErrors = e ? (e.graphQLErrors || (e.body ? e.body.errors : [])) : [];
                const errorInfo = errorCode && graphQLErrors.length > 0 ? graphQLErrors[0].userInfo : null;

                this.logger.error(`Error updating/creating user (${errorCode})`, e);

                if(errorCode === ApiError.USER_EMAIL_EXISTS){
                    this.$toast.error('Het gekozen e-mail adres is al in gebruik');
                }
                else if (errorCode === ApiError.PASSWORD_SHORT) {
                    this.$toast.error(`Het wachtwoord dient uit minimaal ${errorInfo.minLength} tekens te bestaan`);
                }
                else if (errorCode === ApiError.PASSWORD_WEAK) {
                    this.$toast.error(`Het gekozen wachtwoord is te zwak, gebruik minimaal 1 kleine letter, 1 hoofdletter en 1 cijfer en/of leesteken`);
                }
                else if (errorCode === ApiError.PASSWORD_NOT_UNIQUE) {
                    this.$toast.error(`Het gekozen wachtwoord is recentelijk al gebruikt, verzin een nieuw wachtwoord`);
                }
                else {
                    this.$toast.error('De gebruiker kon niet worden opgeslagen');
                }
            }

            this.submitting = false;
        }

        async sendNewPassword() {

            const { result, modal } = await this.$di.alert.confirm('Weet je zeker dat je deze gebruiker een wachtwoord reset e-mail wilt toezenden?', true);
            if(!result){
                return;
            }

            try {

                await UserPasswordRequestMutations.forgotPassword(this.user.email);

                this.$modal.close(modal);
                this.$toast.success('De e-mail is verzonden');
            }
            catch(e) {
                this.logger.error(`Error sending password new to users`, e);

                this.$modal.close(modal);
                this.$toast.error('Er kon geen e-mail worden verzonden naar de gebruiker');
            }
        }

        async deleteUser(){

            const { result, modal } = await this.$di.alert.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?', true);

            if(!result){
                return;
            }

            try {

                await UserMutations.delete(this.user.id);

                this.logger.info(`User ${this.user.id} deleted`);

                this.$modal.close(modal);
                this.$toast.success('De gebruiker is verwijderd');

                this.$router.replace({
                    name: 'users'
                });
            }
            catch(e){

                this.logger.error(`Error deleting user ${this.user.id}`, e);

                this.$modal.close(modal);
                this.$toast.error('De gebruiker kon niet worden verwijderd');
            }
        }

        getApplicationRoles(application) {

            return Application.applicationRoles()[application].map(role => ({
                label: userRoleFilter(role),
                value: role
            }));
        }

        get isSettingNewPassword() {

            return !!this.editUser.password?.length;
        }

        get applications() {

            return Object.keys(Application);
        }

        get isUpdating() {

            return !!this.userId;
        }

        get userId() {

            return this.user.id;
        }

        get isAdmin() {

            return this.$user.role === UserRole.ADMIN
        }
    }

</script>
